import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import { UserProfileHoverCard } from "../community/UserProfileHoverCard";
import { LevelBadge } from "./LevelBadge";
import { Trophy } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

interface UserPointsData {
  id: string;
  user_id: string;
  total_points: number;
  current_level: number;
  profile: {
    full_name: string | null;
    logo_url?: string | null;
  } | null;
}

function getInitials(name: string | null | undefined): string {
  if (!name) return "U";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

const MEDAL_STYLES = [
  { bg: "from-yellow-500/15 to-amber-500/5 border-yellow-500/30", medal: "🥇", ring: "ring-yellow-400/40" },
  { bg: "from-slate-300/15 to-gray-400/5 border-slate-400/30", medal: "🥈", ring: "ring-slate-300/40" },
  { bg: "from-orange-600/15 to-amber-700/5 border-orange-600/30", medal: "🥉", ring: "ring-orange-500/40" },
];

function TopThreeCard({ user, index }: { user: UserPointsData; index: number }) {
  const style = MEDAL_STYLES[index];
  return (
    <div className={`relative flex flex-col items-center gap-2 rounded-xl border bg-gradient-to-b ${style.bg} p-4 md:p-6 flex-1 min-w-0`}>
      <span className="text-2xl md:text-3xl">{style.medal}</span>
      <Avatar className={`h-12 w-12 md:h-14 md:w-14 ring-2 ${style.ring}`}>
        {user.profile?.logo_url ? (
          <AvatarImage src={user.profile.logo_url} alt={user.profile?.full_name || "Usuário"} />
        ) : null}
        <AvatarFallback className="text-sm font-semibold">{getInitials(user.profile?.full_name)}</AvatarFallback>
      </Avatar>
      <UserProfileHoverCard userId={user.user_id}>
        <span className="text-sm md:text-base font-semibold text-foreground hover:text-primary cursor-pointer transition-colors text-center line-clamp-2">
          {user.profile?.full_name || "Usuário Anônimo"}
        </span>
      </UserProfileHoverCard>
      <LevelBadge userId={user.user_id} />
      <span className="text-lg md:text-xl font-bold text-primary">{user.total_points} pts</span>
    </div>
  );
}

function RankRow({ user, rank }: { user: UserPointsData; rank: number }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors">
      <span className="w-6 text-center text-sm font-semibold text-muted-foreground">{rank}</span>
      <Avatar className="h-8 w-8">
        {user.profile?.logo_url ? (
          <AvatarImage src={user.profile.logo_url} alt={user.profile?.full_name || "Usuário"} />
        ) : null}
        <AvatarFallback className="text-xs">{getInitials(user.profile?.full_name)}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <UserProfileHoverCard userId={user.user_id}>
          <span className="text-sm font-medium text-foreground hover:text-primary cursor-pointer transition-colors truncate">
            {user.profile?.full_name || "Usuário Anônimo"}
          </span>
        </UserProfileHoverCard>
        <LevelBadge userId={user.user_id} />
      </div>
      <span className="text-sm font-bold text-primary whitespace-nowrap">{user.total_points} pts</span>
    </div>
  );
}

export function Leaderboard() {
  const [userPoints, setUserPoints] = useState<UserPointsData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchUserPoints() {
      try {
        const { data: adminRoles } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('role', 'admin');
        const { data: adminProfiles } = await supabase
          .from('profiles')
          .select('id')
          .eq('is_admin', true);
        const adminIds = new Set([
          ...(adminRoles?.map(r => r.user_id) || []),
          ...(adminProfiles?.map(p => p.id) || []),
        ]);

        const { data: pointsData, error } = await supabase
          .from('user_points')
          .select('id, user_id, total_points, current_level, profile:public_profiles(full_name, logo_url)')
          .order('total_points', { ascending: false })
          .limit(20);

        if (error) throw error;

        const filtered = (pointsData || [])
          .filter(u => !adminIds.has(u.user_id))
          .slice(0, 10);

        setUserPoints(filtered);
      } catch (error) {
        console.error('Error loading leaderboard data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchUserPoints();
  }, []);

  const topThree = userPoints.slice(0, 3);
  const rest = userPoints.slice(3);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-primary" />
          Ranking da Comunidade
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {Array(5).fill(null).map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-[150px]" />
                  <Skeleton className="h-4 w-[120px]" />
                </div>
              </div>
            ))}
          </div>
        ) : userPoints.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            Nenhum dado de pontuação encontrado
          </div>
        ) : (
          <div className="space-y-4">
            {/* Top 3 */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {topThree.map((user, i) => (
                <TopThreeCard key={user.id} user={user} index={i} />
              ))}
            </div>

            {/* Rest */}
            {rest.length > 0 && (
              <div className="border-t pt-3 space-y-0.5">
                {rest.map((user, i) => (
                  <RankRow key={user.id} user={user} rank={i + 4} />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
